/**
 * Passkey Management UI
 */

import { isWebAuthnSupported, registerPasskey } from "./webauthn-client";
import { iconHtml } from "./icons";
import { showToast } from "./web-components";

interface PasskeyCredential {
  id: string;
  credential_id_b64: string;
  created_at: number;
}

async function loadPasskeys(): Promise<void> {
  try {
    const response = await fetch("/api/auth/me", { credentials: "include" });
    if (!response.ok) return;

    const userData = (await response.json()) as { passkeys?: PasskeyCredential[] };
    renderPasskeyList(userData.passkeys || []);
  } catch (error) {
    console.error("Failed to load passkeys:", error);
  }
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function renderPasskeyList(passkeys: PasskeyCredential[]): void {
  const passkeyList = document.getElementById("passkeyList");
  if (!passkeyList) return;

  passkeyList.replaceChildren();

  if (passkeys.length === 0) {
    return;
  }

  for (const passkey of passkeys) {
    const card = document.createElement("div");
    card.className = "auth-method-card";
    card.innerHTML = `
      <div class="auth-method-left">
        <div class="auth-method-icon passkey">
          ${iconHtml("key-round", 24)}
        </div>
        <div class="auth-method-details">
          <h4>Passkey</h4>
          <p>${passkey.credential_id_b64.substring(0, 20)}... · ${formatDate(passkey.created_at)}</p>
        </div>
      </div>
    `;

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "btn btn-ghost-danger";
    deleteBtn.textContent = "Remove";
    deleteBtn.addEventListener("click", async () => {
      if (confirm("Delete this passkey?")) {
        await deletePasskey(passkey.credential_id_b64);
      }
    });

    card.appendChild(deleteBtn);
    passkeyList.appendChild(card);
  }
}

async function deletePasskey(id: string): Promise<void> {
  try {
    const response = await fetch(`/api/auth/me/passkey/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to delete passkey");
    showToast({ message: "Passkey deleted", type: "success" });
    await loadPasskeys();
  } catch (error) {
    showToast({
      message: error instanceof Error ? error.message : "Failed to delete passkey",
      type: "error",
    });
  }
}

function showModal(): void {
  if (!isWebAuthnSupported()) {
    showToast({ message: "Your browser does not support passkeys", type: "error" });
    return;
  }
  document.getElementById("passkeyModal")?.classList.add("active");
}

function closeModal(): void {
  document.getElementById("passkeyModal")?.classList.remove("active");
}

async function handleRegister(): Promise<void> {
  const btn = document.getElementById("passkeyRegisterBtn") as HTMLButtonElement;
  if (!btn) return;

  btn.disabled = true;
  btn.textContent = "Registering...";

  try {
    await registerPasskey("");
    showToast({ message: "Passkey registered successfully!", type: "success" });
    await loadPasskeys();
    closeModal();
  } catch (error) {
    showToast({
      message: error instanceof Error ? error.message : "Failed to register passkey",
      type: "error",
    });
  } finally {
    btn.disabled = false;
    btn.textContent = "Register Passkey";
  }
}

export function initPasskeyManagement(): void {
  document.getElementById("addPasskeyBtn")?.addEventListener("click", showModal);
  document.getElementById("closeModalBtn")?.addEventListener("click", closeModal);
  document.getElementById("passkeyRegisterBtn")?.addEventListener("click", handleRegister);
  document.getElementById("passkeyModal")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeModal();
  });
  loadPasskeys();
}
